/**
 * Appointment Grouping Utility
 *
 * Groups identical appointments by their therapy set. Two appointments are
 * "identical" when they deliver the same prescription lines, in any order and
 * with the same multiplicity — so a single KG and a Doppelbehandlung of KG are
 * different sets, and the same pair arriving in either order is one set.
 *
 * ORDERING: this is the CANONICAL-IDENTITY axis — order-independent by design,
 * so one therapy set is one group however its slots happen to be arranged.
 * See `docs/therapy-ordering.md` before changing anything here.
 *
 * Both halves DELEGATE rather than reimplement, and must keep doing so: the
 * signature to `canonicalTherapyKey`, the label to `slotTherapyList`. Each used
 * to roll its own and each drifted from the rest of the app in its own way —
 * see the two functions for what that cost.
 */

import { canonicalTherapyKey, slotTherapyList } from "./slotTherapy";
export interface AppointmentGroup<T = any> {
  /** Order-independent identity of the therapy set — prescription line ids. */
  signature: string;
  /** Human-readable label, e.g. "KG, PA". Reads exactly as the cards do. */
  label: string;
  /** All appointments in this group */
  appointments: T[];
  /** How many appointments share this signature */
  count: number;
  /** The first appointment – used as "representative" for display */
  representative: T;
}

/**
 * The IDENTITY of an appointment's therapy set — two appointments group into one
 * stack when this matches.
 *
 * Delegates to `canonicalTherapyKey`, which keys on the prescription LINE id.
 * This used to build its own key from `abbreviation` + `rxNumber`, which is
 * display text an admin can rename: renaming a Heilmittel silently re-grouped
 * every stack that contained it. That is the same D-RX-05 trap MD-232 removed
 * from the colour lookup, and identity must never be keyed on renameable text.
 *
 * The line id subsumes the Rx number it used to carry — a line belongs to
 * exactly one Rx — and is strictly more precise: two appointments delivering KG
 * from two DIFFERENT lines of the same Rx are now separate stacks, where the
 * abbreviation key merged them. They are separate work with separate remaining
 * quantities, and the shelf (which has always used `canonicalTherapyKey`)
 * already showed them apart.
 *
 * Multiplicity survives, so a Doppelbehandlung does not collapse into a single
 * KG group: the key carries one entry per slot.
 */
export const getTherapySignature = (appointment: any): string => {
  const timeSlots = appointment?.timeSlots ?? [];
  if (timeSlots.length === 0) return "no-timeslots";

  return canonicalTherapyKey(timeSlots);
};

/**
 * The stack's therapy label — "3 identische Termine (KG, KG)".
 *
 * Goes through `slotTherapyList`, the one way an appointment's therapies are
 * collapsed into a string, because this caption sits directly above the very
 * cards it describes and has to say what they say.
 *
 * It used to sort alphabetically and deduplicate with a Set, which broke that
 * on two counts: a Doppelbehandlung read "KG" over a card listing KG twice, and
 * a main therapy sorting after its supplement read "BAN, MT" over a card
 * showing MT first. Both agreed only when the main therapy happened to sort
 * first alphabetically (KG before PA), which is why it looked right.
 */
export const getTherapyLabel = (appointment: any): string =>
  slotTherapyList(appointment?.timeSlots) || "–";

/**
 * Groups an array of appointments by their therapy signature.
 * Returns an array of AppointmentGroup, preserving the order of first occurrence.
 */
export const groupAppointmentsBySignature = <T extends Record<string, any>>(
  appointments: T[]
): AppointmentGroup<T>[] => {
  if (!appointments || appointments.length === 0) return [];

  const groupMap = new Map<string, AppointmentGroup<T>>();
  const order: string[] = [];

  for (const appt of appointments) {
    const sig = getTherapySignature(appt);
    const existing = groupMap.get(sig);
    if (existing) {
      existing.appointments.push(appt);
      existing.count++;
    } else {
      order.push(sig);
      groupMap.set(sig, {
        signature: sig,
        label: getTherapyLabel(appt),
        appointments: [appt],
        count: 1,
        representative: appt,
      });
    }
  }

  return order.map((sig) => groupMap.get(sig)!);
};

/**
 * Same as groupAppointmentsBySignature but for CancelledAppointmentDto
 * which wraps the real appointment inside `.appointment`.
 */
export const getTherapySignatureCancelled = (cancelledAppointment: any): string => {
  return getTherapySignature(cancelledAppointment?.appointment);
};

export const groupCancelledAppointmentsBySignature = <T extends Record<string, any>>(
  appointments: T[]
): AppointmentGroup<T>[] => {
  if (!appointments || appointments.length === 0) return [];

  const groupMap = new Map<string, AppointmentGroup<T>>();
  const order: string[] = [];

  for (const appt of appointments) {
    const sig = getTherapySignatureCancelled(appt);
    const existing = groupMap.get(sig);
    if (existing) {
      existing.appointments.push(appt);
      existing.count++;
    } else {
      order.push(sig);
      groupMap.set(sig, {
        signature: sig,
        label: getTherapyLabel(appt?.appointment),
        appointments: [appt],
        count: 1,
        representative: appt,
      });
    }
  }

  return order.map((sig) => groupMap.get(sig)!);
};
